import { createActions, createReducer } from 'reduxsauce';
import ImmutableObject from 'seamless-immutable';

/* ------------- Types and Action Creators ------------- */

export interface GalleryImage {
  id: string;
  url: string;
  title: string;
  description: string;
  date: string;
  category: string;
  goalId?: string;
  childId?: string;
  uploadedAt?: string;
  fileSize?: number;
  isFavorite?: boolean;
}

export interface GalleryState extends ImmutableObject<{
  data: GalleryImage[];
  loading: boolean;
  error: boolean;
  errorMessage?: string;
  filters: {
    searchQuery: string;
    selectedCategories: string[];
    dateRange: {
      start: string;
      end: string;
    };
    sortBy: string;
  };
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
  };
}> {}

const { Types, Creators } = createActions({
  // Load gallery data
  loadGalleryRequest: ['filters'],
  loadGallerySuccess: ['data', 'pagination'],
  loadGalleryFailure: ['errorMessage'],
  
  // Image actions
  uploadImageRequest: ['image', 'goalId'],
  uploadImageSuccess: ['image'],
  uploadImageFailure: ['errorMessage'],
  
  deleteImageRequest: ['imageId'],
  deleteImageSuccess: ['imageId'],
  deleteImageFailure: ['errorMessage'],
  
  // Filter and search
  updateFilters: ['filters'],
  clearFilters: null,
  
  // Pagination
  setCurrentPage: ['page'],
  setItemsPerPage: ['itemsPerPage'],
  
  // Favorites
  toggleFavorite: ['imageId'],
  toggleFavoriteSuccess: ['imageId', 'isFavorite'],
  toggleFavoriteFailure: ['errorMessage'],
  
  // Reset state
  resetGallery: null,
});

export const GalleryTypes = Types;
export const GalleryActions = Creators;

/* ------------- Initial State ------------- */

export const INITIAL_STATE: GalleryState = ImmutableObject({
  data: [],
  loading: false,
  error: false,
  errorMessage: undefined,
  filters: {
    searchQuery: '',
    selectedCategories: [],
    dateRange: {
      start: '',
      end: '',
    },
    sortBy: 'date',
  },
  pagination: {
    currentPage: 1,
    itemsPerPage: 20,
    totalItems: 0,
    totalPages: 0,
  },
});

/* ------------- Reducers ------------- */

export const loadGalleryRequest = (state: GalleryState) => {
  return {
    ...state,
    loading: true,
    error: false,
    errorMessage: undefined,
  };
};

export const loadGallerySuccess = (
  state: GalleryState,
  { data, pagination }: { data: GalleryImage[]; pagination: any }
) => {
  return {
    ...state,
    data,
    pagination,
    loading: false,
    error: false,
    errorMessage: undefined,
  };
};


export const loadGalleryFailure = (state: GalleryState, { errorMessage }: { errorMessage: string }) => {
  const updateData = { 
    loading: false, 
    error: true, 
    errorMessage 
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const uploadImageRequest = (state: GalleryState) => {
  return state.merge ? state.merge({ loading: true, error: false }) : { ...state, loading: true, error: false };
};

export const uploadImageSuccess = (state: GalleryState, { image }: { image: GalleryImage }) => {
  const newData = [...state.data, image];
  const updateData = { 
    data: newData, 
    loading: false, 
    error: false,
    pagination: {
      ...state.pagination,
      totalItems: state.pagination.totalItems + 1,
      totalPages: Math.ceil((state.pagination.totalItems + 1) / state.pagination.itemsPerPage)
    }
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const uploadImageFailure = (state: GalleryState, { errorMessage }: { errorMessage: string }) => {
  const updateData = { 
    loading: false, 
    error: true, 
    errorMessage 
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const deleteImageRequest = (state: GalleryState) => {
  return state.merge ? state.merge({ loading: true, error: false }) : { ...state, loading: true, error: false };
};

export const deleteImageSuccess = (state: GalleryState, { imageId }: { imageId: string }) => {
  const newData = state.data.filter((img: GalleryImage) => img.id !== imageId);
  const updateData = { 
    data: newData, 
    loading: false, 
    error: false,
    pagination: {
      ...state.pagination,
      totalItems: Math.max(0, state.pagination.totalItems - 1),
      totalPages: Math.ceil(Math.max(0, state.pagination.totalItems - 1) / state.pagination.itemsPerPage)
    }
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const deleteImageFailure = (state: GalleryState, { errorMessage }: { errorMessage: string }) => {
  const updateData = { 
    loading: false, 
    error: true, 
    errorMessage 
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const updateFilters = (state: GalleryState, { filters }: { filters: any }) => {
  const updateData = { 
    filters: { ...state.filters, ...filters },
    pagination: { ...state.pagination, currentPage: 1 } // Reset to first page when filters change
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const clearFilters = (state: GalleryState) => {
  const updateData = { 
    filters: INITIAL_STATE.filters,
    pagination: { ...state.pagination, currentPage: 1 }
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const setCurrentPage = (state: GalleryState, { page }: { page: number }) => {
  const updateData = { 
    pagination: { ...state.pagination, currentPage: page }
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const setItemsPerPage = (state: GalleryState, { itemsPerPage }: { itemsPerPage: number }) => {
  const updateData = { 
    pagination: { 
      ...state.pagination, 
      itemsPerPage,
      currentPage: 1, // Reset to first page
      totalPages: Math.ceil(state.pagination.totalItems / itemsPerPage)
    }
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const toggleFavorite = (state: GalleryState) => {
  return state.merge ? state.merge({ loading: true, error: false }) : { ...state, loading: true, error: false };
};

export const toggleFavoriteSuccess = (state: GalleryState, { imageId, isFavorite }: { imageId: string, isFavorite: boolean }) => {
  const newData = state.data.map((img: GalleryImage) => 
    img.id === imageId ? { ...img, isFavorite } : img
  );
  const updateData = { 
    data: newData, 
    loading: false, 
    error: false 
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const toggleFavoriteFailure = (state: GalleryState, { errorMessage }: { errorMessage: string }) => {
  const updateData = { 
    loading: false, 
    error: true, 
    errorMessage 
  };
  return state.merge ? state.merge(updateData) : { ...state, ...updateData };
};

export const resetGallery = () => {
  return INITIAL_STATE;
};

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.LOAD_GALLERY_REQUEST]: loadGalleryRequest,
  [Types.LOAD_GALLERY_SUCCESS]: loadGallerySuccess,
  [Types.LOAD_GALLERY_FAILURE]: loadGalleryFailure,
  [Types.UPLOAD_IMAGE_REQUEST]: uploadImageRequest,
  [Types.UPLOAD_IMAGE_SUCCESS]: uploadImageSuccess,
  [Types.UPLOAD_IMAGE_FAILURE]: uploadImageFailure,
  [Types.DELETE_IMAGE_REQUEST]: deleteImageRequest,
  [Types.DELETE_IMAGE_SUCCESS]: deleteImageSuccess,
  [Types.DELETE_IMAGE_FAILURE]: deleteImageFailure,
  [Types.UPDATE_FILTERS]: updateFilters,
  [Types.CLEAR_FILTERS]: clearFilters,
  [Types.SET_CURRENT_PAGE]: setCurrentPage,
  [Types.SET_ITEMS_PER_PAGE]: setItemsPerPage,
  [Types.TOGGLE_FAVORITE]: toggleFavorite,
  [Types.TOGGLE_FAVORITE_SUCCESS]: toggleFavoriteSuccess,
  [Types.TOGGLE_FAVORITE_FAILURE]: toggleFavoriteFailure,
  [Types.RESET_GALLERY]: resetGallery,
});
